if (!customElements.get('delivery-location')) {
    class DeliveryLocation extends HTMLElement {

        /**
         * Constructor for the DeliveryLocation class
         */
        constructor() {
            super();
            this.localStorageKey = 'userLocationData';
            this.userLocationData = null;
            this.isMobile = window.innerWidth < 1024;
            document.addEventListener('DOMContentLoaded', () => this.init());
        }

        connectedCallback() {
            // Use connectedCallback – by this point the element is in the DOM.
            this.init();
        }

        init() {
            // Query within the component’s DOM
            this.addressElement = this.querySelector('#located-address');
            this.tooltipElement = this.querySelector('.delivery-tooltip');

            if (!this.addressElement) {
                console.error('No element with id "located-address" found.');
                return;
            }

            this.addressElement.innerText = 'Locating address...';
            this.userLocationData = this.getValidUserLocationData();

            if (this.userLocationData) {
                this.initDeliveryLocationLogic();
            } else {
                // Listen for the geolocation:updated event and trigger lookup if needed
                document.addEventListener(
                    'geolocation:updated',
                    (event) => {
                        this.userLocationData = event.detail.userLocationData;
                        this.initDeliveryLocationLogic();
                    },
                    { once: true }
                );

                if (navigator.userAgent.match(/bot|spider/i)) {
                    // It's a bot!
                } else {
                    // It's not a bot!
                    this.getUserLocationData();
                }
            }

            // Set up tooltip behavior
            if (this.isMobile) {
                this.mobileTooltipShow();
            } else {
                this.handleHoverForTooltip();
            }
        }

        /**
         * Retrieves the valid user location data from localStorage.
         * @returns {{expiryTime}|any|null}
         */
        getValidUserLocationData() {
            try {
                const storedValue = localStorage.getItem(this.localStorageKey);
                if (storedValue) {
                    const data = JSON.parse(storedValue);
                    if (data.expiryTime && Date.now() <= data.expiryTime) {
                        return data;
                    }
                    localStorage.removeItem(this.localStorageKey);
                }
            } catch (error) {
                console.error('Error parsing location data:', error);
            }
            return null;
        }

        /**
         * Initializes the delivery location logic.
         */
        initDeliveryLocationLogic() {
            if (this.userLocationData) {
                this.fillAddressElement(
                    this.userLocationData.city,
                    this.userLocationData.zip,
                    this.userLocationData.precise
                );
                this.setListenerForPreciseLocation(this.userLocationData.precise);
            } else {
                if(navigator.userAgent.match(/bot|spider/i)) {
                    // It's a bot!
                } else {
                    // It's not a bot!
                    this.getUserLocationData();
                }
            }
        }

        /**
         * Fetches the user location data from the IP address.
         */
        getUserLocationData() {

            // For testing purposes, you can simulate a new request every time.
            console.log('NEW ADDITIONAL REQUEST!!!!!!');

            fetch("https://ipinfo.io/json")
                .then((response) => {
                    if (!response.ok) {
                        throw new Error(`HTTP error: ${response.status}`);
                    }
                    return response.json();
                })
                .then((data) => {
                    if (data && data.loc) {
                        const [latitude, longitude] = data.loc.split(',');
                        this.updateCustomerLocationData(
                            data.city,
                            data.region,
                            data.country,
                            data.postal,
                            latitude,
                            longitude,
                            false
                        );
                        this.fillAddressElement(data.city, data.postal, false);
                    } else {
                        this.addressElement.innerText =
                            'Failed to locate address. Click to try again';
                    }
                })
                .catch((error) => {
                    console.error('Error fetching primary address:', error);
                    this.addressElement.innerText =
                        'Failed to locate address. Click to try again';
                })
                .finally(() => {
                    this.userLocationData = this.getValidUserLocationData();
                    this.setListenerForPreciseLocation(this.userLocationData?.precise);
                });
        }

        /**
         * Fetches the precise location of the user and updates the address element.
         */
        getPreciseLocation() {
            this.addressElement.innerText = 'Locating address...';
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        const { latitude, longitude } = position.coords;
                        this.getAddressFromCoordinates(latitude, longitude);
                    },
                    () => {
                        this.addressElement.innerText = 'Geolocation failed. ' +
                            'Please allow access to your location in browser settings and click to try again';
                        if (this.tooltipElement) this.tooltipElement.remove();
                    }
                );
            } else {
                this.addressElement.innerText =
                    'Geolocation is not supported by this browser';
                if (this.tooltipElement) this.tooltipElement.remove();
            }
        }

        /**
         * Fetches the address details from the given latitude and longitude coordinates.
         * @param latitude
         * @param longitude
         */
        getAddressFromCoordinates(latitude, longitude) {
            const apiKey = 'AIzaSyBVESorBeiBk0QfN_PtdExy28gEK8nTFBk';
            const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${apiKey}&language=en`;

            fetch(url)
                .then((response) => response.json())
                .then((data) => {
                    if (data.status !== 'OK' || !data.results.length) {
                        throw new Error(`Google Maps API error: ${data.status}`);
                    }
                    const addressComponents = data.results[0].address_components;
                    const city = addressComponents.find((comp) => comp.types.includes('locality'))?.long_name;
                    const postalCode = addressComponents.find((comp) => comp.types.includes('postal_code'))?.long_name;
                    const region = addressComponents.find((comp) => comp.types.includes('administrative_area_level_1'))?.long_name;
                    const country = addressComponents.find((comp) => comp.types.includes('country'))?.long_name;

                    if (!city || !postalCode || !region || !country) {
                        throw new Error('Incomplete address details received.');
                    }

                    this.updateCustomerLocationData(
                        city,
                        region,
                        country,
                        postalCode,
                        latitude,
                        longitude,
                        true
                    );
                    this.fillAddressElement(city, postalCode, true);
                    this.setListenerForPreciseLocation(true);

                    const preciseUserLocationData = {
                        city: city,
                        region: region,
                        country: country,
                        zip: postalCode,
                        latitude: parseFloat(latitude),
                        longitude: parseFloat(longitude),
                        precise: true,
                        expiryTime: Date.now() + 48 * 60 * 60 * 1000,
                    };

                    document.dispatchEvent(
                        new CustomEvent('geolocation:preciseLocation', {
                            detail: { userLocationData: preciseUserLocationData },
                        })
                    );
                })
                .catch((error) => {
                    console.error('Error fetching address from coordinates:', error);
                    this.addressElement.innerText =
                        'Failed to locate address. Click to try again';
                    if (this.tooltipElement) this.tooltipElement.remove();
                });
        }

        /**
         * Updates the user location data in localStorage and the class property.
         * @param city
         * @param region
         * @param country
         * @param postalCode
         * @param latitude
         * @param longitude
         * @param precise
         */
        updateCustomerLocationData(city, region, country, postalCode, latitude, longitude, precise) {
            const userLocationData = {
                city: city,
                region: region,
                country: country,
                zip: postalCode,
                latitude: parseFloat(latitude),
                longitude: parseFloat(longitude),
                precise,
                expiryTime: Date.now() + 48 * 60 * 60 * 1000,
            };
            localStorage.setItem(this.localStorageKey, JSON.stringify(userLocationData));
            this.userLocationData = userLocationData;
        }

        /**
         * Fills the address element with the given city and postal code.
         * @param city
         * @param postalCode
         * @param precise
         */
        fillAddressElement(city, postalCode, precise) {
            if (!this.addressElement) return;
            this.addressElement.innerHTML = `<span>${city}</span>&nbsp;<span>${postalCode}</span>`;

            if (!precise && this.tooltipElement) {
                this.tooltipElement.classList.remove('hidden');
                this.tooltipElement.innerText = 'Click to get a more precise location';
            } else if (this.tooltipElement) {
                this.tooltipElement.remove();
            }
        }

        /**
         * Sets the click listener for the address element to get a more precise location.
         * @param precise
         */
        setListenerForPreciseLocation(precise) {
            if (!this.addressElement) return;
            if (!precise) {
                this.addressElement.onclick = () => this.getPreciseLocation();
            } else {
                this.addressElement.onclick = null;
            }
        }

        /**
         * Shows a tooltip on mobile devices to prompt users to tap for a more precise location
         */
        mobileTooltipShow() {
            if (!this.tooltipElement) {
                console.error('Tooltip element not found.');
                return;
            }
            const observer = new IntersectionObserver(
                (entries, observer) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            setTimeout(() => {
                                this.tooltipElement.innerText =
                                    'Tap to get a more precise location';
                                this.tooltipElement.classList.add('show');
                                setTimeout(() => {
                                    this.tooltipElement.classList.remove('show');
                                    this.tooltipElement.addEventListener(
                                        'transitionend',
                                        () => {
                                            this.tooltipElement.remove();
                                            observer.disconnect();
                                        },
                                        { once: true }
                                    );
                                }, 4000);
                            }, 1000);
                        }
                    });
                },
                {
                    root: null,
                    rootMargin: '0px',
                    threshold: 1,
                }
            );
            observer.observe(this.tooltipElement);
        }


        /**
         * Handles hover events for tooltip
         */
         handleHoverForTooltip() {
            const tooltipElement = document.querySelector('.delivery-tooltip');
            const triggerElement = tooltipElement ? tooltipElement.previousElementSibling : null;

            if (!tooltipElement || !triggerElement) {
                return;
            }

            // Event listeners for mouseenter and mouseleave
            triggerElement.addEventListener('mouseenter', toggleTooltip);
            triggerElement.addEventListener('mouseleave', toggleTooltip);

            /**
             * Sub-function for toggling tooltip visibility
             * @param event
             */
            function toggleTooltip(event) {
                tooltipElement.classList.toggle('show', event.type === 'mouseenter');
            }

            // Cleanup function for removing event listeners
            return function cleanup() {
                triggerElement.removeEventListener('mouseenter', toggleTooltip);
                triggerElement.removeEventListener('mouseleave', toggleTooltip);
            };
        }

    }
    customElements.define('delivery-location', DeliveryLocation);
}